import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Loader2, User as UserIcon, Upload, Camera, ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';
import { Sport, SPORT_LABELS } from '@/types';
import { SkillLevel, SKILL_LEVEL_LABELS } from './SkillLevelBadge';

const profileSchema = z.object({
  name: z.string().trim().min(1, 'Tên không được để trống').max(100, 'Tên tối đa 100 ký tự'),
  player_id: z.string()
    .min(3, 'ID tối thiểu 3 ký tự')
    .max(8, 'ID tối đa 8 ký tự')
    .regex(/^[A-Za-z0-9]+$/, 'ID chỉ chứa chữ cái và số')
    .transform(val => val.toUpperCase()),
  location: z.string().max(200, 'Địa điểm tối đa 200 ký tự').optional().or(z.literal('')),
  age: z.number().min(1, 'Tuổi phải lớn hơn 0').max(120, 'Tuổi không hợp lệ').optional().nullable(),
  gender: z.enum(['male', 'female', 'other']).optional().nullable(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Tables<'profiles'>;
  onSave: (updates: Partial<Tables<'profiles'>>) => Promise<{ error: unknown }>;
}

const AVAILABLE_SPORTS: Sport[] = ['football', 'volleyball', 'badminton', 'tabletennis', 'tennis', 'pickleball', 'basketball', 'swimming', 'golf', 'billiards', 'running', 'cycling'];
const AVAILABLE_SKILL_LEVELS: SkillLevel[] = ['beginner', 'amateur', 'semi_pro', 'professional'];

export function EditProfileDialog({ open, onOpenChange, profile, onSave }: EditProfileDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [selectedSports, setSelectedSports] = useState<Sport[]>((profile.sports || []) as Sport[]);
  const [selectedRankedSport, setSelectedRankedSport] = useState<Sport | null>((profile as any).ranked_sport as Sport | null);
  const [rankedSkillLevel, setRankedSkillLevel] = useState<SkillLevel>((profile.skill_level || 'amateur') as SkillLevel);
  const [showRankResetWarning, setShowRankResetWarning] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<ProfileFormData | null>(null);
  const [previewAvatar, setPreviewAvatar] = useState<string | null>(profile.avatar);
  const [previewCover, setPreviewCover] = useState<string | null>((profile as any).cover_image);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [playerIdError, setPlayerIdError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  
  const originalRankedSport = (profile as any).ranked_sport as Sport | null;
  
  const { register, handleSubmit, formState: { errors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: profile.name,
      player_id: (profile as any).player_id || '',
      location: profile.location || '',
      age: profile.age,
      gender: profile.gender as 'male' | 'female' | 'other' | null,
    },
  });

  const toggleSport = (sport: Sport) => {
    setSelectedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Chỉ chấp nhận file ảnh (JPEG, PNG, WebP, GIF)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Kích thước file tối đa 5MB');
      return;
    }

    setAvatarFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewAvatar(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Chỉ chấp nhận file ảnh (JPEG, PNG, WebP, GIF)');
      return;
    }

    // Validate file size (10MB max for cover)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Kích thước file tối đa 10MB');
      return;
    }

    setCoverFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewCover(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return previewAvatar;

    setIsUploading(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // Delete old avatar if exists and is from storage
      if (profile.avatar?.includes('avatars')) {
        const oldPath = profile.avatar.split('/avatars/')[1]?.split('?')[0];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Không thể tải lên ảnh đại diện');
      return profile.avatar;
    } finally {
      setIsUploading(false);
    }
  };

  const uploadCover = async (): Promise<string | null> => {
    if (!coverFile || !user) return previewCover;

    setIsUploadingCover(true);
    try {
      const fileExt = coverFile.name.split('.').pop();
      const fileName = `${user.id}/cover_${Date.now()}.${fileExt}`;

      // Delete old cover if exists and is from storage
      const oldCover = (profile as any).cover_image;
      if (oldCover?.includes('avatars')) {
        const oldPath = oldCover.split('/avatars/')[1]?.split('?')[0];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, coverFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Cover upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading cover:', error);
      toast.error('Không thể tải lên ảnh bìa');
      return (profile as any).cover_image;
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleRankedSportChange = (sport: Sport | null) => {
    if (sport !== originalRankedSport && originalRankedSport !== null) {
      setShowRankResetWarning(true);
    } else {
      setShowRankResetWarning(false);
    }
    setSelectedRankedSport(sport);
  };

  const checkPlayerIdUnique = async (playerId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('player_id', playerId.toUpperCase())
      .neq('id', profile.id)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking player_id:', error);
      return false;
    }
    
    return data === null;
  };

  const onSubmit = async (data: ProfileFormData) => {
    setPlayerIdError(null);
    
    // Check if player_id is unique
    const isUnique = await checkPlayerIdUnique(data.player_id);
    if (!isUnique) {
      setPlayerIdError('ID này đã được sử dụng, vui lòng chọn ID khác');
      return;
    }
    
    // Check if ranked sport changed - need confirmation
    const rankedSportChanged = selectedRankedSport !== originalRankedSport && originalRankedSport !== null;
    
    if (rankedSportChanged) {
      setPendingFormData(data);
      setShowConfirmDialog(true);
      return;
    }

    await saveProfile(data);
  };

  const saveProfile = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      // Upload images if there are new files
      const avatarUrl = avatarFile ? await uploadAvatar() : previewAvatar;
      const coverUrl = coverFile ? await uploadCover() : previewCover;

      // Check if ranked sport changed - need to reset rank points
      const rankedSportChanged = selectedRankedSport !== originalRankedSport;

      const updates: Partial<Tables<'profiles'>> & { 
        cover_image?: string | null; 
        ranked_sport?: Sport | null;
        rank_points?: number;
        rank?: string;
        player_id?: string;
      } = {
        name: data.name,
        player_id: data.player_id.toUpperCase(),
        avatar: avatarUrl,
        cover_image: coverUrl,
        location: data.location || null,
        age: data.age || null,
        gender: data.gender || null,
        sports: selectedSports,
        ranked_sport: selectedRankedSport,
        skill_level: rankedSkillLevel,
      };

      // Reset rank points if ranked sport changed
      if (rankedSportChanged) {
        updates.rank_points = 0;
        updates.rank = 'bronze';
      }

      const { error } = await onSave(updates);
      
      if (error) {
        toast.error('Không thể cập nhật hồ sơ');
        return;
      }
      
      toast.success('Đã cập nhật hồ sơ');
      // Invalidate caches that depend on skill_level / ranked_sport so MatchCard,
      // MatchDetail, NearbyMatchCard re-evaluate eligibility immediately.
      queryClient.invalidateQueries({ queryKey: ['my-rank-profile'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['nearby-matches'] });
      queryClient.invalidateQueries({ queryKey: ['match'] });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRankReset = async () => {
    setShowConfirmDialog(false);
    if (pendingFormData) {
      await saveProfile(pendingFormData);
      setPendingFormData(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa hồ sơ</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Cover Image Upload */}
          <div className="space-y-2">
            <Label>Ảnh bìa</Label>
            <div 
              className="relative w-full h-32 rounded-xl bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed border-border cursor-pointer group hover:border-primary/50 transition-colors"
              onClick={() => coverInputRef.current?.click()}
            >
              {previewCover ? (
                <img src={previewCover} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <ImageIcon className="w-8 h-8" />
                  <span className="text-sm">Nhấn để chọn ảnh bìa</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-8 h-8 text-white" />
              </div>
              {isUploadingCover && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleCoverSelect}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground">Khuyến nghị kích thước: 1200x400px • Tối đa 10MB</p>
          </div>

          {/* Avatar Upload */}
          <div className="flex flex-col items-center gap-3">
            <Label className="self-start">Ảnh đại diện</Label>
            <div 
              className="relative w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-primary/20 cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              {previewAvatar ? (
                <img src={previewAvatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-10 h-10 text-muted-foreground" />
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-6 h-6 text-white" />
              </div>
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              Tải ảnh lên
            </Button>
            <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, GIF • Tối đa 5MB</p>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Tên hiển thị *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Nhập tên của bạn"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Player ID */}
          <div className="space-y-2">
            <Label htmlFor="player_id">ID người chơi *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">#</span>
              <Input
                id="player_id"
                {...register('player_id')}
                placeholder="VD: ABC123"
                className="pl-7 uppercase"
                maxLength={8}
              />
            </div>
            {errors.player_id && (
              <p className="text-sm text-destructive">{errors.player_id.message}</p>
            )}
            {playerIdError && (
              <p className="text-sm text-destructive">{playerIdError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              ID duy nhất để người khác tìm và mời bạn • 3-8 ký tự chữ/số
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Địa điểm</Label>
            <Input
              id="location"
              {...register('location')}
              placeholder="VD: Hà Nội, Việt Nam"
            />
            {errors.location && (
              <p className="text-sm text-destructive">{errors.location.message}</p>
            )}
          </div>

          {/* Age */}
          <div className="space-y-2">
            <Label htmlFor="age">Tuổi</Label>
            <Input
              id="age"
              type="number"
              {...register('age', { valueAsNumber: true })}
              placeholder="Nhập tuổi của bạn"
            />
            {errors.age && (
              <p className="text-sm text-destructive">{errors.age.message}</p>
            )}
          </div>

          {/* Gender */}
          <div className="space-y-2">
            <Label htmlFor="gender">Giới tính</Label>
            <select
              id="gender"
              {...register('gender')}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
            >
              <option value="">Chọn giới tính</option>
              <option value="male">Nam</option>
              <option value="female">Nữ</option>
              <option value="other">Khác</option>
            </select>
          </div>

          {/* Ranked Sport Selection */}
          <div className="space-y-2">
            <Label>Môn thể thao tính điểm Rank</Label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors",
                  selectedRankedSport === null 
                    ? "border-primary bg-primary/10" 
                    : "border-input hover:border-primary/50"
                )}
              >
                <input
                  type="radio"
                  name="ranked_sport"
                  value=""
                  checked={selectedRankedSport === null}
                  onChange={() => handleRankedSportChange(null)}
                  className="sr-only"
                />
                <span className="text-sm">Chưa chọn</span>
              </label>
              {AVAILABLE_SPORTS.map((sport) => (
                <label
                  key={sport}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors",
                    selectedRankedSport === sport 
                      ? "border-primary bg-primary/10" 
                      : "border-input hover:border-primary/50"
                  )}
                >
                  <input
                    type="radio"
                    name="ranked_sport"
                    value={sport}
                    checked={selectedRankedSport === sport}
                    onChange={() => handleRankedSportChange(sport)}
                    className="sr-only"
                  />
                  <span className="text-sm">{SPORT_LABELS[sport]}</span>
                </label>
              ))}
            </div>
            {showRankResetWarning && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  ⚠️ Thay đổi môn thể thao tính điểm sẽ reset điểm rank về 0 (Bronze)
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Bạn chỉ có thể tham gia trận rank với môn thể thao đã chọn
            </p>
          </div>

          {/* Ranked Skill Level - only show if ranked sport is selected */}
          {selectedRankedSport && (
            <div className="space-y-2">
              <Label>Trình độ thi đấu Rank ({SPORT_LABELS[selectedRankedSport]})</Label>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_SKILL_LEVELS.map((level) => (
                  <label
                    key={level}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors",
                      rankedSkillLevel === level 
                        ? "border-primary bg-primary/10" 
                        : "border-input hover:border-primary/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="ranked_skill_level"
                      value={level}
                      checked={rankedSkillLevel === level}
                      onChange={() => setRankedSkillLevel(level)}
                      className="sr-only"
                    />
                    <span className="text-sm">{SKILL_LEVEL_LABELS[level]}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Bạn chỉ có thể thi đấu rank với người chơi cùng trình độ
              </p>
            </div>
          )}

          {/* Sports */}
          <div className="space-y-2">
            <Label>Môn thể thao</Label>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_SPORTS.map((sport) => (
                <label
                  key={sport}
                  className="flex items-center gap-2 p-2 rounded-lg border border-input hover:border-primary/50 cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={selectedSports.includes(sport)}
                    onCheckedChange={() => toggleSport(sport)}
                  />
                  <span className="text-sm">{SPORT_LABELS[sport]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isUploading || isUploadingCover}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              variant="gradient"
              className="flex-1"
              disabled={isSubmitting || isUploading || isUploadingCover}
            >
              {isSubmitting || isUploading || isUploadingCover ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isUploading || isUploadingCover ? 'Đang tải ảnh...' : 'Đang lưu...'}
                </>
              ) : (
                'Lưu thay đổi'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Confirmation Dialog for Rank Reset */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Xác nhận thay đổi môn thể thao</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Bạn đang thay đổi môn thể thao tính điểm rank từ{' '}
                <strong>{originalRankedSport ? SPORT_LABELS[originalRankedSport] : 'chưa chọn'}</strong>{' '}
                sang{' '}
                <strong>{selectedRankedSport ? SPORT_LABELS[selectedRankedSport] : 'không chọn'}</strong>.
              </p>
              <p className="text-destructive font-medium">
                Hành động này sẽ reset toàn bộ điểm rank về 0 (Bronze) và không thể hoàn tác!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingFormData(null)}>
              Hủy bỏ
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmRankReset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Xác nhận thay đổi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
